import { Outlet, useLocation } from "react-router-dom";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CommandPalette } from "@/layout/CommandPalette";
import { Sidebar } from "@/layout/Sidebar";
import { Topbar } from "@/layout/Topbar";
import { useIsMobile } from "@/hooks/useIsMobile";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { useUIStore } from "@/store/ui";
import { useEffect } from "react";

export function Shell() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const mobileNavOpen = useUIStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);
  const setPaletteOpen = useUIStore((s) => s.setPaletteOpen);
  const setSuperAdmin = useAuthStore((s) => s.setSuperAdmin);
  const userEmail = useAuthStore((s) => s.user?.email);
  const location = useLocation();
  const isMobile = useIsMobile();

  // Hydrate the cross-tenant super-admin flag once per session.
  useEffect(() => {
    if (!userEmail) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<{ is_super_admin: boolean }>(
          "/superadmin/whoami",
        );
        if (!cancelled) setSuperAdmin(!!data.is_super_admin);
      } catch {
        // 401/403/network — treat as not super admin and stay silent.
        if (!cancelled) setSuperAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userEmail, setSuperAdmin]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setPaletteOpen]);

  // Close the mobile drawer on every navigation so a tap on a nav item
  // collapses the drawer + reveals the destination in one motion.
  useEffect(() => {
    if (mobileNavOpen) setMobileNavOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // On mobile the sidebar floats over the content as a drawer; main column
  // takes the full width. On desktop the sidebar reserves its 240/56 column.
  const mainMarginLeft = isMobile ? 0 : collapsed ? 56 : 240;
  const mainPadding = isMobile ? 16 : 24;

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--canvas)" }}>
      <Sidebar />
      {isMobile && mobileNavOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setMobileNavOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(8,8,8,0.45)",
            border: "none",
            zIndex: 9,
            cursor: "pointer",
          }}
        />
      )}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          marginLeft: mainMarginLeft,
          transition: "margin-left 120ms ease-out",
        }}
      >
        <Topbar />
        <main style={{ flex: 1, padding: mainPadding, minWidth: 0 }}>
          {/* `key={location.pathname}` resets the boundary on route change so a crash
              on one page doesn't poison navigation back to a working one. */}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}
